/**
 * Customer opt-in for order-status Web Push.
 *
 * Everything here runs only from an explicit user gesture on the Order Details page. Nothing in
 * this module is invoked on application startup, on login, or when an order is opened — the one
 * exception is {@link readOrderNotificationsMarker}, a pure `localStorage` read used to render
 * the correct label, which touches no browser notification API.
 *
 * Wire contract (backend `CustomerPushSubscriptionController`):
 *   POST   api/orders/v1/customer/push-subscriptions  { restaurant_id, endpoint, keys }
 *   DELETE api/orders/v1/customer/push-subscriptions  { endpoint }
 * The owning customer is derived from the authenticated JWT. No customerId/userId is ever sent —
 * a browser-supplied identity would let a caller register against someone else's account.
 */
import type { AxiosResponse } from "axios";
import API from "@/utils/api";
import { API_PATHS } from "@/constants/api-paths";
import {
  PUSH_SERVICE_WORKER_PATH,
  PUSH_SERVICE_WORKER_SCOPE,
  getNotificationPermission,
  getVapidPublicKey,
  isWebPushSupported,
  urlBase64ToUint8Array,
} from "@/config/web-push";
import { isValidStorefrontBasePath } from "@/utils/storefront-paths";

/** Why an opt-in attempt did not end in an active, backend-registered subscription. */
export type EnableOrderNotificationsFailure =
  | "unsupported"
  | "permission-denied"
  | "permission-dismissed"
  | "not-configured"
  | "service-worker-failed"
  | "subscribe-failed"
  | "backend-failed";

export type EnableOrderNotificationsResult =
  | { ok: true }
  | { ok: false; reason: EnableOrderNotificationsFailure };

/**
 * Which browser subscription this device last registered with RasoiX.
 *
 * There is no GET on the backend contract, so this local marker is the only way to tell
 * "enabled" from "this browser has a push subscription that RasoiX no longer knows about"
 * (which is exactly the state sign-out leaves behind). It holds no customer identity.
 */
type OrderNotificationsMarker = {
  endpoint: string;
  restaurantId: string;
};

const MARKER_STORAGE_KEY = "rx-order-notifications";

export function readOrderNotificationsMarker(): OrderNotificationsMarker | null {
  try {
    const raw = window.localStorage.getItem(MARKER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OrderNotificationsMarker> | null;
    const endpoint = typeof parsed?.endpoint === "string" ? parsed.endpoint : "";
    const restaurantId = typeof parsed?.restaurantId === "string" ? parsed.restaurantId : "";
    if (!endpoint || !restaurantId) return null;
    return { endpoint, restaurantId };
  } catch {
    // Private mode / corrupted value — treat as "not enabled on this browser".
    return null;
  }
}

function writeOrderNotificationsMarker(marker: OrderNotificationsMarker): void {
  try {
    window.localStorage.setItem(MARKER_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    /* private mode — the opt-in still worked, only the label may reset */
  }
}

/** Called on sign-out / session invalidation: this browser is no longer a known RasoiX device. */
export function clearOrderNotificationsMarker(): void {
  try {
    window.localStorage.removeItem(MARKER_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

/**
 * Service worker URL for the current storefront entry.
 *
 * The `base` query is read by the worker's `notificationclick` handler when no RasoiX tab is
 * open. Scope stays "/" either way, so this remains one service worker for the origin.
 */
export function pushServiceWorkerUrl(basePath: string): string {
  const base = isValidStorefrontBasePath(basePath) ? basePath : "";
  if (!base) return PUSH_SERVICE_WORKER_PATH;
  return `${PUSH_SERVICE_WORKER_PATH}?base=${encodeURIComponent(base)}`;
}

/**
 * Notification permission request that also supports the legacy callback signature
 * (older Safari), resolving to `"default"` when the prompt is dismissed or unavailable.
 */
function requestNotificationPermission(): Promise<NotificationPermission> {
  return new Promise<NotificationPermission>((resolve) => {
    let settled = false;
    const settle = (permission: NotificationPermission) => {
      if (settled) return;
      settled = true;
      resolve(permission === "granted" || permission === "denied" ? permission : "default");
    };

    try {
      const maybePromise = window.Notification.requestPermission(settle) as
        | Promise<NotificationPermission>
        | undefined;
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.then(settle, () => settle("default"));
      }
    } catch {
      settle("default");
    }
  });
}

/** Registers the single push service worker and waits for it to become active. */
async function registerPushServiceWorker(basePath: string): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(pushServiceWorkerUrl(basePath), {
    scope: PUSH_SERVICE_WORKER_SCOPE,
  });
  if (registration.active) return registration;
  // `pushManager.subscribe` requires an active worker.
  return navigator.serviceWorker.ready;
}

/**
 * Reuses the browser's existing subscription when present — the backend upsert is keyed on the
 * endpoint, so re-enabling after a sign-out re-associates the same browser rather than churning
 * push-service registrations.
 */
async function resolvePushSubscription(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;

  const applicationServerKey = urlBase64ToUint8Array(getVapidPublicKey());
  if (!applicationServerKey) return null;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
}

function isSuccessfulEnvelope(response: AxiosResponse<unknown> | undefined): boolean {
  if (!response || typeof response.status !== "number") return false;
  if (response.status < 200 || response.status >= 300) return false;

  const envelope = response.data as { success?: unknown } | undefined;
  if (envelope && typeof envelope === "object" && "success" in envelope) {
    return envelope.success === true;
  }
  return true;
}

/**
 * Posts the browser subscription verbatim (its own `toJSON()` shape) plus the restaurant the
 * order belongs to. Returns false on any non-success so the caller never claims "enabled".
 */
async function registerSubscriptionWithBackend(
  subscription: PushSubscription,
  restaurantId: string
): Promise<boolean> {
  const json = subscription.toJSON();
  const endpoint = typeof json.endpoint === "string" ? json.endpoint : "";
  const p256dh = json.keys?.p256dh ?? "";
  const auth = json.keys?.auth ?? "";
  if (!endpoint || !p256dh || !auth) return false;

  try {
    const response = await API.post(
      API_PATHS.CUSTOMER_PUSH_SUBSCRIPTIONS,
      {
        restaurant_id: restaurantId,
        endpoint,
        keys: { p256dh, auth },
      },
      { skipErrorToast: true }
    );
    return isSuccessfulEnvelope(response);
  } catch {
    return false;
  }
}

/**
 * The full opt-in: request permission → register worker → subscribe → POST. Every failure
 * resolves to a reason; nothing here throws into the page.
 *
 * Permission is requested FIRST, as the opt-in click's first await. Safari requires
 * `Notification.requestPermission()` to run inside the transient user activation the click
 * granted, and awaiting a service worker registration first can outlive that window on a cold
 * first registration — which would make the first click fail and only the second one work.
 */
export async function enableOrderNotifications(input: {
  restaurantId: string;
  storefrontBasePath: string;
}): Promise<EnableOrderNotificationsResult> {
  const restaurantId = input.restaurantId.trim();
  if (!isWebPushSupported()) return { ok: false, reason: "unsupported" };
  if (!restaurantId) return { ok: false, reason: "not-configured" };
  if (!getVapidPublicKey()) return { ok: false, reason: "not-configured" };
  if (getNotificationPermission() === "denied") return { ok: false, reason: "permission-denied" };

  const permission = await requestNotificationPermission();
  if (permission === "denied") return { ok: false, reason: "permission-denied" };
  if (permission !== "granted") return { ok: false, reason: "permission-dismissed" };

  let registration: ServiceWorkerRegistration;
  try {
    registration = await registerPushServiceWorker(input.storefrontBasePath);
  } catch {
    return { ok: false, reason: "service-worker-failed" };
  }

  let subscription: PushSubscription | null;
  try {
    subscription = await resolvePushSubscription(registration);
  } catch {
    return { ok: false, reason: "subscribe-failed" };
  }
  if (!subscription) return { ok: false, reason: "subscribe-failed" };

  const registered = await registerSubscriptionWithBackend(subscription, restaurantId);
  if (!registered) return { ok: false, reason: "backend-failed" };

  writeOrderNotificationsMarker({ endpoint: subscription.endpoint, restaurantId });
  return { ok: true };
}

/**
 * True when this browser holds a push subscription that RasoiX was told about, for this
 * restaurant. Passive: reads an existing registration but never registers one, and never
 * touches `Notification.requestPermission`.
 */
export async function isOrderNotificationsEnabled(restaurantId: string): Promise<boolean> {
  const id = restaurantId.trim();
  if (!id || !isWebPushSupported()) return false;
  if (getNotificationPermission() !== "granted") return false;

  const marker = readOrderNotificationsMarker();
  if (!marker || marker.restaurantId !== id) return false;

  try {
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SERVICE_WORKER_SCOPE);
    if (!registration) return false;
    const subscription = await registration.pushManager.getSubscription();
    return Boolean(subscription && subscription.endpoint === marker.endpoint);
  } catch {
    return false;
  }
}

/**
 * Sign-out: drops the RasoiX customer↔browser association only.
 *
 * `PushSubscription.unsubscribe()` is deliberately NOT called. The browser-level subscription
 * belongs to the browser, not to a RasoiX session — the next customer to sign in on this device
 * opts in again over the same endpoint, and revoking it here would throw away a browser-level
 * grant the customer never asked us to give up. The backend DELETE exists for exactly this.
 *
 * Best-effort with a short timeout: sign-out must never be blocked or broken by push cleanup.
 */
export async function revokeOrderNotificationsForSession(): Promise<void> {
  try {
    if (!isWebPushSupported()) return;

    const marker = readOrderNotificationsMarker();
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SERVICE_WORKER_SCOPE);
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    const endpoint = subscription?.endpoint || marker?.endpoint || "";
    if (!endpoint) return;

    await API.delete(API_PATHS.CUSTOMER_PUSH_SUBSCRIPTIONS, {
      data: { endpoint },
      timeout: 4000,
      skipErrorToast: true,
    });
  } catch {
    /* best effort — the local marker is still cleared by session cleanup */
  }
}
